import React, { createContext, useContext, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { translations, getAlternateRoute, routeMap, languages } from './translations';

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();

  // Determine active language from URL path
  const lang = useMemo(() => {
    return location.pathname.startsWith('/en') ? 'en' : 'da';
  }, [location.pathname]);

  const t = useMemo(() => {
    return translations[lang] || translations.da;
  }, [lang]);

  // Navigate to corresponding route in target language
  const switchLanguage = (targetLang) => {
    if (targetLang === lang) return;
    const targetPath = getAlternateRoute(location.pathname, targetLang);
    navigate(targetPath);
  };

  // Get direct link to the alternate page for SEO <Link> or <a> tags
  const getSwitchUrl = (targetLang) => {
    return getAlternateRoute(location.pathname, targetLang);
  };

  // Get URL for a named route in current language
  const getRouteUrl = (key, targetLanguage = lang) => {
    if (routeMap[key] && routeMap[key][targetLanguage]) {
      return routeMap[key][targetLanguage];
    }
    return targetLanguage === 'en' ? '/en' : '/';
  };

  const value = {
    lang,
    t,
    languages,
    switchLanguage,
    getSwitchUrl,
    getRouteUrl,
    currentPath: location.pathname,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
